import { ResizeManager } from '@theme/event-manager';
import {
  createSlider,
  getObserver,
  layoutRecalcManager,
  layoutPhaseMeasure,
  layoutPhaseApply,
} from '@theme/theme-global';

if (!customElements.get('announcement-bar')) {
  class AnnouncementBar extends HTMLDivElement {
    constructor() {
      super();
      this.section = this.closest('.shopify-section');
      this._lastStaticHeight = null;
      this._lastDynamicHeight = null;
      this._isInViewport = false;
      this._observer = getObserver({ threshold: [0] }, false, true);
      this._onIntersection = this._onIntersection.bind(this);
      this._recalculateHeights = this._recalculateHeights.bind(this);
      this._requestRecalc = () => layoutRecalcManager.request();
    }

    connectedCallback() {
      this._observer.observe(this, this._onIntersection);
      ResizeManager.subscribe(this._requestRecalc);
      layoutRecalcManager.register(this._recalculateHeights);
      // Single batched measure→apply (no immediate + double-rAF request)
      layoutRecalcManager.request();
    }

    disconnectedCallback() {
      this._observer.unobserve(this);
      ResizeManager.unsubscribe(this._requestRecalc);
      layoutRecalcManager.unregister(this._recalculateHeights);
      document.body.style.setProperty('--announcementHeight', `0px`);
      document.body.style.setProperty('--dynamicAnnouncementHeight', `0px`);
    }

    _recalculateHeights(phase) {
      if (layoutPhaseMeasure(phase)) {
        const rect = this.getBoundingClientRect();
        this._pendingStaticHeight = rect.height;
        this._pendingDynamicHeight = rect.bottom > 0 && rect.top < window.innerHeight ? rect.height : 0;
        this._pendingInViewport = rect.bottom > 0 && rect.top < window.innerHeight;
      }

      if (layoutPhaseApply(phase)) {
        this._applyStaticHeight(this._pendingStaticHeight);
        if (this._pendingInViewport != null) {
          this._isInViewport = this._pendingInViewport;
        }
        this._setDynamicHeight(this._pendingDynamicHeight ?? 0);
      }
    }

    _applyStaticHeight(height) {
      if (height == null || height === this._lastStaticHeight) return;

      this._lastStaticHeight = height;
      document.body.style.setProperty('--announcementHeight', `${height}px`);
    }

    _setDynamicHeight(height) {
      if (height === this._lastDynamicHeight) return;

      this._lastDynamicHeight = height;
      document.body.style.setProperty('--dynamicAnnouncementHeight', `${height}px`);
    }

    _onIntersection(entry) {
      this._isInViewport = entry.isIntersecting;

      if (!entry.isIntersecting) {
        this._setDynamicHeight(0);
        return;
      }
      if (this._lastStaticHeight != null) {
        this._setDynamicHeight(this._lastStaticHeight);
        return;
      }
      layoutRecalcManager.request();
    }
  }

  customElements.define('announcement-bar', AnnouncementBar, { extends: 'div' });
}

if (!customElements.get('slider-element')) {
  class AnnouncementBarSlider extends HTMLDivElement {
    constructor() {
      super();
      this.section = this.closest('.shopify-section');
    }

    connectedCallback() {
      if (this.querySelectorAll('.swiper-slide').length < 2) return;
      if (this.sliderInstance) return;

      const params = {
        ...JSON.parse(this.dataset.params || '{}'),
        bypassLcpGate: true,
      };

      createSlider(this, params).then((instance) => {
        if (!this.isConnected) {
          instance?.destroy?.(true, true);
          return;
        }
        this.sliderInstance = instance;
      });
    }

    disconnectedCallback() {
      this.sliderInstance?.destroy?.(true, true);
      this.sliderInstance = null;
    }
  }

  customElements.define('slider-element', AnnouncementBarSlider, { extends: 'div' });
}
